/**
 * useAdminPicksMetrics Hook
 * Fetches aggregate metrics for the Picks Data Dashboard
 * Separate from picks list to enable independent loading and caching
 */

import type {
  GroupBy,
  ParlayMetrics,
  PicksFilters,
  PicksMetrics,
  ViewMode,
} from '@server/services/admin/adminPicksTypes.ts';
import { useCallback, useEffect, useState } from 'react';

export interface UseAdminPicksMetricsParams {
  viewMode: ViewMode;
  groupBy: GroupBy;
  filters: PicksFilters;
}

export interface UseAdminPicksMetricsResult {
  metrics: PicksMetrics | ParlayMetrics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch aggregate metrics for current filter set
 * Automatically refetches when filters change
 */
export function useAdminPicksMetrics(
  params: UseAdminPicksMetricsParams,
): UseAdminPicksMetricsResult {
  const { viewMode, groupBy, filters } = params;

  const [metrics, setMetrics] = useState<PicksMetrics | ParlayMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch metrics from API
   */
  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams({
        viewMode,
        groupBy,
      });

      if (Object.keys(filters).length > 0) {
        params.set('filters', JSON.stringify(filters));
      }

      const response = await fetch(`/api/admin/picks/metrics?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.statusText}`);
      }

      const result: PicksMetrics | ParlayMetrics = await response.json();
      setMetrics(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  }, [viewMode, groupBy, filters]);

  /**
   * Refetch metrics
   */
  const refetch = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Fetch metrics when filters change
   */
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics,
    loading,
    error,
    refetch,
  };
}
